import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import {
  ReportModel,
  ReportType,
  SharedDriveModel,
  UserRole,
} from '../../../../../types';
import { ReportFormValues } from '../reports-form-page';
import SharedTransferList from '../../../../shared/shared-transfer-list';
import AlertList from '../../../../shared/alert-list';
import { Dispatch, SetStateAction, useMemo } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Link from '@mui/material/Link';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
interface StepSourcesProps {
  control: Control<ReportFormValues>;
  setValue: UseFormSetValue<ReportFormValues>;
  watch: UseFormWatch<ReportFormValues>;
  errors: FieldErrors<ReportFormValues>;
  domains: string[];
  trigger: UseFormTrigger<ReportFormValues>;
  selectedReportType: ReportType | null;
  clearErrors: UseFormClearErrors<ReportFormValues>;
  roles: UserRole[];
  setSelectedSharedDrives: Dispatch<SetStateAction<SharedDriveModel[]>>;
  reports?: ReportModel[];
  selectedRowId?: number;
  onViewReport: (r: ReportModel) => void;
}

export default function StepSourcesColumns({
  control,
  setValue,
  watch,
  errors,
  domains,
  trigger,
  roles,
  selectedReportType,
  clearErrors,
  setSelectedSharedDrives,
  reports,
  selectedRowId,
  onViewReport,
}: StepSourcesProps) {
  const sources = watch('sources') || [];
  const sourceType = watch('sourceType');
  const allSource = watch('allSource');
  const reportType = watch('reportType');

  // Reactive duplicate check: fires whenever sources change (e.g. after ">" is clicked)
  const duplicateReports = useMemo(() => {
    if (!reports || !reportType || sources.length === 0) return [];
    const sourcesSet = new Set(sources);
    return reports.filter((report) => {
      if (report.id === selectedRowId) return false;
      if (report.reportType !== reportType) return false;
      const reportSources = report.sources ?? [];
      if (reportSources.length === 0) return false;
      if (report.sourceType !== sourceType) return false;
      if (reportSources.length !== sources.length) return false;
      return reportSources.every((s) => sourcesSet.has(s));
    });
  }, [reports, selectedRowId, reportType, sources, sourceType]);

  const isResourceCalendarReport =
    selectedReportType?.name == 'recurringResourceCalendarEventsReport';
  // Adjustable manually for report type 'sharedDrivesDocsSharedExternally'
  const sharedDrivesDocsSharedExternallyMaxSelection = 10;

  function addSelection(emails: string[]) {
    const uniqueEmails = Array.from(new Set([...sources!, ...emails]));
    setValue('sources', uniqueEmails);
    clearErrors('sources');
    if (uniqueEmails.length === 0) {
      trigger('sources');
    }
  }

  function removeSelection(emails: string[]) {
    const newList = sources!.filter((userEmail) => !emails.includes(userEmail));
    setValue('sources', newList);
  }

  const handleAddAllChecked = (value: boolean) => {
    setValue('allSource', value);
    if (value) {
      setValue('sources', []);
      clearErrors('sources');
    }
  };
  const hasSharedDriveSourceType =
    selectedReportType?.name === 'sharedDrivesDocsSharedExternally';

  // Source type check - if initial load source type is shared drive or null but 'sharedDrivesDocsSharedExternally' should show shared drive
  const isSharedDriveReport =
    (sourceType === 'Shared_Drive' || !sourceType) && hasSharedDriveSourceType;

  return (
    <Box
      sx={{
        viewTransitionName: 'steps',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap={2} sx={{ flexShrink: 0, height: '100%' }}>
        {selectedReportType?.name == 'recurringResourceCalendarEventsReport' ? (
          <Typography sx={{ mb: 1 }}>
            Please select at least one resource calendar.
          </Typography>
        ) : sourceType === 'Shared_Drive' ? (
          sharedDrivesDocsSharedExternallyMaxSelection > 0 ? (
            <Typography sx={{ mb: 1 }}>
              Please select up to {sharedDrivesDocsSharedExternallyMaxSelection}{' '}
              shared drives.
            </Typography>
          ) : (
            <Typography sx={{ mb: 1 }}>
              Please select atleast one shared drive.
            </Typography>
          )
        ) : (
          <Typography sx={{ mb: 1 }}>
            Please select at least one source to gather data from for your
            report.
          </Typography>
        )}
        <Stack
          flexDirection={'row'}
          gap={4}
          alignItems={'center'}
          justifyContent="center"
        >
          <Controller
            name={`sourceType`}
            control={control}
            render={({ field: props }) => {
              return (
                <FormControl>
                  <FormLabel>
                    {sources!.length > 0
                      ? 'Source Type (Only one source type is allowed)'
                      : 'Source Type'}
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="radio-buttons-group-label"
                    defaultValue={undefined}
                    name="radio-buttons-group"
                    value={props.value}
                    onChange={(e) => {
                      props.onChange(e.target.value);
                      setValue('sources', []);
                    }}
                    row
                  >
                    {!isSharedDriveReport && !isResourceCalendarReport && (
                      <>
                        <Tooltip
                          arrow
                          title={
                            !selectedReportType!.possibleSources.includes(
                              'User'
                            )
                              ? 'Not applicable to this report type.'
                              : ''
                          }
                        >
                          <FormControlLabel
                            value="User"
                            control={<Radio />}
                            label="User"
                            disabled={
                              sources!.length > 0 ||
                              !selectedReportType!.possibleSources.includes(
                                'User'
                              ) ||
                              allSource === true
                            }
                          />
                        </Tooltip>
                        <Tooltip
                          arrow
                          title={
                            !selectedReportType!.possibleSources.includes(
                              'Group'
                            )
                              ? 'Not applicable to this report type.'
                              : ''
                          }
                        >
                          <FormControlLabel
                            value="Group"
                            control={<Radio />}
                            label="Group"
                            disabled={
                              sources!.length > 0 ||
                              !selectedReportType!.possibleSources.includes(
                                'Group'
                              ) ||
                              allSource === true
                            }
                          />
                        </Tooltip>
                        <Tooltip
                          arrow
                          title={
                            !selectedReportType!.possibleSources.includes(
                              'OrgUnit'
                            )
                              ? 'Not applicable to this report type.'
                              : ''
                          }
                        >
                          <FormControlLabel
                            value="OrgUnit"
                            control={<Radio />}
                            label="Org Unit"
                            disabled={
                              sources!.length > 0 ||
                              !selectedReportType!.possibleSources.includes(
                                'OrgUnit'
                              ) ||
                              allSource === true
                            }
                          />
                        </Tooltip>
                        <Tooltip
                          arrow
                          title={
                            !selectedReportType!.possibleSources.includes(
                              'Domain'
                            )
                              ? 'Not applicable to this report type.'
                              : ''
                          }
                        >
                          <FormControlLabel
                            value="Domain"
                            control={<Radio />}
                            label="Domain"
                            disabled={
                              sources!.length > 0 ||
                              !selectedReportType!.possibleSources.includes(
                                'Domain'
                              ) ||
                              allSource === true
                            }
                          />
                        </Tooltip>
                        {selectedReportType?.name === 'gPanelRoleReport' ? (
                          <Tooltip
                            arrow
                            title={
                              !selectedReportType!.possibleSources.includes(
                                'gPanel_Role'
                              )
                                ? 'Not applicable to this report type.'
                                : ''
                            }
                          >
                            <FormControlLabel
                              value="gPanel_Role"
                              control={<Radio />}
                              label="Role"
                              disabled={
                                sources!.length > 0 ||
                                !selectedReportType!.possibleSources.includes(
                                  'gPanel_Role'
                                ) ||
                                allSource === true
                              }
                            />
                          </Tooltip>
                        ) : null}
                      </>
                    )}
                    {!isSharedDriveReport && isResourceCalendarReport && (
                      <Tooltip
                        arrow
                        title={
                          !selectedReportType!.possibleSources.includes(
                            'Resource_Calendar'
                          )
                            ? 'Not applicable to this report type.'
                            : ''
                        }
                      >
                        <FormControlLabel
                          value="Resource_Calendar"
                          control={<Radio />}
                          label="Resource Calendar"
                          disabled={
                            sources!.length > 0 ||
                            !selectedReportType!.possibleSources.includes(
                              'Resource_Calendar'
                            ) ||
                            allSource === true
                          }
                        />
                      </Tooltip>
                    )}

                    {hasSharedDriveSourceType && (
                      <Tooltip
                        arrow
                        title={
                          !selectedReportType!.possibleSources.includes(
                            'Shared_Drive'
                          )
                            ? 'Not applicable to this report type.'
                            : ''
                        }
                      >
                        <FormControlLabel
                          value="Shared_Drive"
                          control={<Radio />}
                          label="Shared Drive"
                          disabled={
                            sources!.length > 0 ||
                            !selectedReportType!.possibleSources.includes(
                              'Shared_Drive'
                            )
                          }
                        />
                      </Tooltip>
                    )}
                  </RadioGroup>
                </FormControl>
              );
            }}
          />
        </Stack>
        <Box
          sx={{
            flex: 1,
            minHeight: 210,
            width: '100%',
          }}
        >
          <Controller
            name={'sources'}
            control={control}
            render={() => (
              <SharedTransferList
                sources={sources}
                addSelection={addSelection}
                removeSelection={removeSelection}
                handleAddAllChecked={handleAddAllChecked}
                errors={!!errors.sources}
                helperText={errors?.sources?.message || ' '}
                allSource={allSource}
                domains={domains || []}
                sourceType={sourceType || ''}
                hasSelectAllOption={true}
                roles={roles || []}
                maxSelection={
                  sourceType === 'Shared_Drive' && hasSharedDriveSourceType
                    ? sharedDrivesDocsSharedExternallyMaxSelection
                    : undefined
                }
                setSelectedSharedDrives={setSelectedSharedDrives}
              />
            )}
          />
        </Box>
        {duplicateReports.length > 0 && (
          <AlertList
            severity="info"
            items={duplicateReports}
            itemsThresholdBeforeScroll={4}
            message={
              <Typography>
                We found {duplicateReports.length} similar{' '}
                <strong>{selectedReportType?.niceName ?? reportType}</strong>{' '}
                {duplicateReports.length === 1 ? 'report' : 'reports'} already
                targeting the selected sources.
              </Typography>
            }
            renderItem={(report: ReportModel) => (
              <Box
                key={report.id}
                sx={{
                  p: 1,
                  width: '100%',
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    fontWeight="bold"
                    noWrap
                    sx={{ flexGrow: 1, textOverflow: 'ellipsis' }}
                  >
                    {report.reportName}
                  </Typography>
                  <Link
                    component="button"
                    variant="body2"
                    type="button"
                    sx={{ flexShrink: 0 }}
                    onClick={() => onViewReport(report)}
                  >
                    View Report
                  </Link>
                </Stack>
              </Box>
            )}
          />
        )}
      </Stack>
    </Box>
  );
}
